import { type FormEvent, useEffect, useMemo, useState } from "react";
import type { Familiarity, Talkativeness } from "../api/types";
import { backend, usePlayer } from "../app/services";
import { storage } from "../lib/storage";
import { useStore } from "../lib/store";
import { useLoad } from "../lib/useLoad";
import type { StationOptions } from "../radio/station";

const LANGUAGES = [
  ["auto", "Auto"],
  ["english", "English"],
  ["russian", "Russian"],
  ["spanish", "Spanish"],
  ["french", "French"],
  ["german", "German"],
  ["japanese", "Japanese"],
  ["korean", "Korean"],
  ["portuguese", "Portuguese"],
  ["chinese", "Chinese"],
] as const;

const TALK: [Talkativeness, string][] = [
  ["low", "Mostly music"],
  ["medium", "Balanced"],
  ["high", "Talk more"],
];

const MUSIC: [Familiarity, string][] = [
  ["familiar", "Familiar"],
  ["blend", "Blend"],
  ["discover", "Discover"],
];

const OPTIONS_KEY = "radiola.station-options";
const DEFAULT_OPTIONS: StationOptions = {
  brief: "",
  language: "auto",
  talkativeness: "medium",
  familiarity: "blend",
  voiceProfileID: "gemini:Kore",
};

function savedOptions(): StationOptions {
  try {
    return { ...DEFAULT_OPTIONS, ...JSON.parse(storage.get(OPTIONS_KEY) ?? "{}") };
  } catch {
    return DEFAULT_OPTIONS;
  }
}

export function StationPage() {
  const { station } = usePlayer();
  const state = useStore(station);

  return (
    <div className="station">
      {state.resumable && state.phase !== "onAir" && (
        <div className="banner">
          <span>Your station is still on air.</span>
          <button type="button" className="primary" onClick={() => void station.resume()}>
            Resume Station
          </button>
        </div>
      )}
      {state.phase === "starting" && <RunSheet />}
      {state.phase === "onAir" && <OnAir />}
      {(state.phase === "idle" || state.phase === "failed") && <StationForm />}
    </div>
  );
}

function StationForm() {
  const { station } = usePlayer();
  const state = useStore(station);
  const [options, setOptions] = useState(savedOptions);
  const voices = useLoad(() => backend.voices());

  useEffect(() => storage.set(OPTIONS_KEY, JSON.stringify(options)), [options]);

  const set = <K extends keyof StationOptions>(key: K, value: StationOptions[K]) =>
    setOptions((current) => ({ ...current, [key]: value }));

  function start() {
    if (options.brief.trim()) void station.start({ ...options, brief: options.brief.trim() });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    start();
  }

  return (
    <form className="card station-form" onSubmit={submit}>
      <h2>What should the station play?</h2>
      <textarea
        rows={4}
        maxLength={2000}
        placeholder="Morning focus: warm electronic, a bit of news about the city, no vocals after ten…"
        value={options.brief}
        onChange={(event) => set("brief", event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) start();
        }}
      />
      <div className="knobs">
        <Segmented
          label="Host"
          options={TALK}
          value={options.talkativeness}
          onChange={(value) => set("talkativeness", value)}
        />
        <Segmented
          label="Music"
          options={MUSIC}
          value={options.familiarity}
          onChange={(value) => set("familiarity", value)}
        />
        <label className="field">
          <span>Language</span>
          <select
            value={options.language}
            onChange={(event) => set("language", event.target.value)}
          >
            {LANGUAGES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Voice</span>
          <select
            value={options.voiceProfileID}
            onChange={(event) => set("voiceProfileID", event.target.value)}
          >
            {!voices.data?.items.some((voice) => voice.id === options.voiceProfileID) && (
              <option value={options.voiceProfileID}>{options.voiceProfileID}</option>
            )}
            {voices.data?.items.map((voice) => (
              <option key={voice.id} value={voice.id}>
                {voice.name} · {voice.provider}
              </option>
            ))}
          </select>
        </label>
      </div>
      {state.error && <p className="error">{state.error}</p>}
      <div className="actions">
        <button type="submit" className="primary" disabled={!options.brief.trim()}>
          Start Radio
        </button>
        <span className="muted">⌘↩</span>
      </div>
    </form>
  );
}

function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: [T, string][];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="segmented">
        {options.map(([option, title]) => (
          <button
            type="button"
            key={option}
            aria-pressed={option === value}
            className={option === value ? "on" : ""}
            onClick={() => onChange(option)}
          >
            {title}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The server's start steps as they happen, like the phone's run-sheet. */
function RunSheet() {
  const { station } = usePlayer();
  const state = useStore(station);
  const stage = useMemo(() => STAGES[state.stage ?? ""] ?? "Starting…", [state.stage]);

  return (
    <div className="card">
      <div className="row between">
        <h2>{stage}</h2>
        <button type="button" className="quiet" onClick={() => void station.cancelStart()}>
          Cancel
        </button>
      </div>
      <div className="progress-indeterminate" />
      <ol className="run-sheet">
        {state.progress.map((entry) => (
          <li key={entry.id}>
            <strong>{entry.title}</strong>
            {entry.detail && <span className="muted"> — {entry.detail}</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

const STAGES: Record<string, string> = {
  admission: "Checking the voice and permissions…",
  queued: "Waiting for the producer…",
  planning: "The producer is planning the show…",
  finding_music: "Finding the music…",
  voicing: "The host is recording…",
  publishing: "Putting it on air…",
  ready: "Starting the music…",
};

function OnAir() {
  const { station, engine } = usePlayer();
  const state = useStore(station);
  const playback = useStore(engine);
  const [directive, setDirective] = useState("");
  const tracksAhead = playback.blocks
    .slice(playback.index + 1)
    .filter((block) => block.kind === "track").length;
  const isPaused = state.runStatus === "paused";
  const isStopped = state.runStatus === "stopped";

  function steer(event: FormEvent) {
    event.preventDefault();
    void station.steer(directive).then(() => setDirective(""));
  }

  return (
    <div className="card on-air">
      <div className="row between">
        <div>
          <p className="eyebrow">
            {isStopped ? "Station ended" : isPaused ? "On air · host paused" : "On air"}
          </p>
          <h2>{state.title}</h2>
        </div>
        <div className="row">
          {!isStopped &&
            (isPaused ? (
              <button type="button" onClick={() => void station.resumeGeneration()}>
                Continue Generating
              </button>
            ) : (
              <button type="button" onClick={() => void station.pauseGeneration()}>
                Stop Generating
              </button>
            ))}
          <button type="button" className="danger" onClick={() => void station.stop()}>
            End Station
          </button>
        </div>
      </div>
      <p className="muted">
        {tracksAhead} track{tracksAhead === 1 ? "" : "s"} queued
        {!isPaused && !isStopped && tracksAhead < 2
          ? " · the producer is preparing the next batch"
          : ""}
      </p>
      {state.notices.map((notice) => (
        <p key={notice} className="notice">
          {notice}
        </p>
      ))}
      {!isStopped && (
        <form className="steer" onSubmit={steer}>
          <input
            placeholder="Steer the show: more jazz, talk about the weather, skip the news…"
            maxLength={300}
            value={directive}
            onChange={(event) => setDirective(event.target.value)}
          />
          <button type="submit" disabled={!directive.trim()}>
            Send
          </button>
        </form>
      )}
      {state.directives.length > 0 && (
        <ul className="chips">
          {state.directives.map((text, index) => (
            <li key={text}>
              {text}
              <button
                type="button"
                aria-label={`Remove “${text}”`}
                onClick={() => void station.removeDirective(index)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {state.error && <p className="error">{state.error}</p>}
    </div>
  );
}
